/**
 * Wealthica2 connection event utilities
 *
 * Responsibilities:
 * - Dispatch a custom connection change event and register listeners
 * - Invalidate related React Query keys when connection state changes
 * - Clear caches on connection error to prevent stale UI
 *
 * Notes:
 * - Pure UI-side orchestration: no network calls; delegates cache clear
 */
import { logger } from '@/services/LoggingService';

// Define constants inline to avoid dependency issues
export const CONNECTION_STATE_CHANGE_EVENT = 'wealthica2_connection_state_change';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearAllCaches } from './cache-management';

/**
 * Event dispatcher for connection changes
 */
export function dispatchConnectionChangeEvent(): void {
  logger.info('ConnectionUtils', 'Dispatching connection change event');
  window.dispatchEvent(new CustomEvent(CONNECTION_STATE_CHANGE_EVENT));
}

/**
 * Hook for managing connection change events
 * @param connectionData The current connection data
 * @param isLoading Loading state
 * @returns Functions to handle connection events
 */
export function useConnectionEvents(connectionData: any | null, isLoading: boolean) {
  const queryClient = useQueryClient();
  
  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['wealthica2', 'accounts'] });
    queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'accounts'] });
    queryClient.invalidateQueries({ queryKey: ['wealthica2', 'positions'] });
    queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'positions'] });
  }, [queryClient]);
  
  const handleConnectionChange = useCallback(() => {
    if (connectionData && !isLoading) {
      logger.info('ConnectionUtils', 'Connection data changed', {
        hasConnection: !!connectionData,
        timestamp: new Date().toISOString()
      });
      
      invalidateQueries();
    }
  }, [connectionData, isLoading, invalidateQueries]);
  
  useEffect(() => {
    window.addEventListener(CONNECTION_STATE_CHANGE_EVENT, handleConnectionChange);
    
    return () => {
      window.removeEventListener(CONNECTION_STATE_CHANGE_EVENT, handleConnectionChange);
    };
  }, [handleConnectionChange]);
  
  // Handle connection error
  useEffect(() => {
    if (connectionData?.error) {
      logger.info('ConnectionUtils', 'Clearing caches due to connection error');
      clearAllCaches();
    }
  }, [connectionData]);
  
  return { invalidateQueries, clearAllCaches };
}
